import { faGripDotsVertical, faPenLine, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { CircularProgress } from "@mui/material";

import { useRef, useState } from "react";

import { useDrag, useDrop } from "react-dnd";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { QuestionnaireStep } from "@/pages/Public/pages/Questionnaire/schema/questionnaire";

interface ViewModeQuestionnaireItemProps {
	data: QuestionnaireStep;
	isEditingInProcess: boolean;
	setIsEditing: (value: number) => void;
	isLoading?: boolean;
	setDeletingQuestionnaire: (value: QuestionnaireStep | null) => void;
	dropQuestion: (dragElementId: number, dragIndex: number, dropIndex: number) => void;
	stepNumber: number;
}

export default function ViewModeQuestionnaireItem({
	data,
	setIsEditing,
	isLoading,
	setDeletingQuestionnaire,
	stepNumber,
	dropQuestion,
	isEditingInProcess,
}: ViewModeQuestionnaireItemProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`manage-questionnaire.${key}`, options);
	const ref = useRef<HTMLDivElement>(null);
	const [dragDirection, setDragDirection] = useState<"up" | "down" | null>(null);
	const [dragStepNumber, setDragStepNumber] = useState(-1);

	const [{ isDragging }, drag] = useDrag({
		type: "QUESTION",
		canDrag: () => !isEditingInProcess,
		item: { stepNumber, originalStepNumber: stepNumber, id: data.id },
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [, drop] = useDrop({
		accept: "QUESTION",
		hover(item: { id: number; originalStepNumber: number; stepNumber: number }, monitor) {
			if (!ref.current || isEditingInProcess) {
				return;
			}

			const dragIndex = item.stepNumber;
			const hoverIndex = stepNumber;

			if (dragIndex === hoverIndex) {
				return;
			}

			const hoverBoundingRect = ref.current.getBoundingClientRect();
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			const clientOffset = monitor.getClientOffset();

			if (!clientOffset) {
				return;
			}

			const hoverClientY = clientOffset.y - hoverBoundingRect.top;

			const newDirection = hoverClientY < hoverMiddleY ? "up" : "down";
			const placeholderIndex = newDirection === "down" ? hoverIndex + 1 : hoverIndex;

			if (dragStepNumber !== hoverIndex || dragDirection !== newDirection) {
				setDragDirection(newDirection);
				setDragStepNumber(placeholderIndex);
			}
		},
		drop(item) {
			dropQuestion(item.id, item.originalStepNumber, stepNumber);
			setDragStepNumber(-1);
			setDragDirection(null);
		},
		collect: (monitor) => {
			if (!monitor.isOver()) {
				setDragStepNumber(-1);
			}
		},
	});

	drag(drop(ref));

	return (
		<div ref={ref} className="w-full space-y-3">
			{dragDirection === "up" && dragStepNumber === stepNumber && (
				<div className="w-full h-20 border border-dashed border-[#4680FC] flex items-center justify-center">
					<p className="text-[16px] text-[#777D8D] text-center">
						{ts("move-to", { stepNumber: dragStepNumber })}
					</p>
				</div>
			)}
			<div
				className={`w-full rounded-xl p-4 bg-white flex gap-3 opacity-${
					isDragging ? "50" : "100"
				} ${isEditingInProcess ? "cursor-not-allowed" : "cursor-pointer"}`}
				style={{ boxShadow: "0px 1px 2px 0px #1C274C1F" }}
			>
				{isLoading && <CircularProgress className="!w-10 !h-10 mx-auto" />}
				{!isLoading && (
					<>
						<Icon className="w-4 h-4 text-[#777D8D]" icon={faGripDotsVertical} />
						<div className="w-full">
							<p className="text-[#777D8D] text-[12px] leading-4">Q{data?.stepNumber}</p>
							<h4 className={`text-primary-blue-dark text-[14px] font-semibold mt-[6px]`}>
								{data.title}
							</h4>
						</div>
						<div className="flex gap-2">
							<Button
								className="rounded-[12px] bg-white !min-w-[44px] !min-h-[44px] !max-w-[44px] !max-h-[44px] !p-0 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
								image={<Icon className="text-primary-blue-dark w-5 h-5" icon={faPenLine} />}
								title=""
								onClick={() => {
									if (data?.id) {
										setIsEditing(data?.id);
									}
								}}
							/>
							<Button
								className="rounded-[12px] bg-white !min-w-[44px] !min-h-[44px] !max-w-[44px] !max-h-[44px] !p-0 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
								image={<Icon className="text-primary-blue-dark w-5 h-5" icon={faTrashCan} />}
								title=""
								onClick={() => {
									if (data?.id) {
										setDeletingQuestionnaire(data);
									}
								}}
							/>
						</div>
					</>
				)}
			</div>

			{dragDirection === "down" && dragStepNumber === stepNumber && (
				<div className="w-full h-20 border border-dashed border-[#4680FC] flex items-center justify-center">
					<p className="text-[16px] text-[#777D8D] text-center">
						{ts("move-to", { stepNumber: dragStepNumber })}
					</p>
				</div>
			)}
		</div>
	);
}
